// Shared BFF auth navigation primitives, used by the auth context (and any future API-client interceptor)
// so the redirect-loop protection stays single-sourced and consistent across the app.
const AUTH_BASE = process.env.REACT_APP_IDP_URL;
const LOGIN_ATTEMPT_KEY = "argus_bff_login_attempt";
const MAX_LOGIN_ATTEMPTS = 3;

// sessionStorage can throw in locked-down contexts (private mode, sandboxed iframe, storage disabled).
// Degrade to no-op loop protection rather than breaking auth.
const safeGetItem = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};
const safeSetItem = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
};
const safeRemoveItem = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
};

// One top-level navigation per page load. Guards StrictMode's double-invoked effects and concurrent
// callers from firing two window.location.assign calls.
let navigating = false;

const isHomePath = () =>
  window.location.pathname === "/" || window.location.pathname === "/home";

export const clearLoginAttempts = () => safeRemoveItem(LOGIN_ATTEMPT_KEY);

// Send the browser to the BFF login. A login from the (unguarded) home page is deliberate — start fresh.
// Elsewhere, count consecutive redirects: if we keep returning still unauthenticated (e.g. a session
// cookie that can't be read cross-site), stop looping and land on home, which offers a Login button.
export const redirectToBffLogin = ({ screenHint, returnTo } = {}) => {
  if (navigating) return;

  const attempts = isHomePath()
    ? 0
    : Number(safeGetItem(LOGIN_ATTEMPT_KEY)) || 0;
  if (attempts >= MAX_LOGIN_ATTEMPTS) {
    safeRemoveItem(LOGIN_ATTEMPT_KEY);
    navigating = true;
    window.location.assign("/");
    return;
  }

  safeSetItem(LOGIN_ATTEMPT_KEY, String(attempts + 1));
  // Prefer an explicit returnTo (e.g. a route-gate's appState) over the current in-place path. Only a
  // single-slash relative path is accepted here; the BFF re-validates before it lands the browser.
  const landing =
    typeof returnTo === "string" &&
    returnTo.startsWith("/") &&
    !returnTo.startsWith("//")
      ? returnTo
      : window.location.pathname + window.location.search;
  const params = new URLSearchParams({ returnTo: landing });
  if (screenHint === "signup") params.set("screenHint", "signup");
  navigating = true;
  window.location.assign(`${AUTH_BASE}/auth/login?${params.toString()}`);
};

export const redirectToBffLogout = () => {
  if (navigating) return;
  safeRemoveItem(LOGIN_ATTEMPT_KEY);
  navigating = true;
  window.location.assign(`${AUTH_BASE}/auth/logout`);
};
